import React, { useMemo } from "react";
import { Table, Button } from "antd";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportCashOuts } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

export const SignOffListReport: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sign-off-list"],
    queryFn: () => GetSalesReportCashOuts({ limit: 100 }),
  });
  const { isAllowed } = useSectionAccess("reports");

  const reportData = useMemo(() => {
    const list = responseData?.data;
    if (Array.isArray(list)) return list;
    if (list?.cash_outs && Array.isArray(list.cash_outs)) return list.cash_outs;
    return [];
  }, [responseData]);

  const stats = useMemo(() => {
    const totalCashClosed = reportData.reduce(
      (sum: number, item: any) => sum + (item.amount || 0),
      0,
    );
    return [
      {
        title: "Total Closed Drawer Shifts",
        value: `${reportData.length} Shifts`,
        colorClass: "text-purple-600",
      },
      {
        title: "Total Cash Drop Audited",
        value: formatLKR(totalCashClosed),
        colorClass: "text-emerald-600",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Closed Time",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Closed By Cashier",
      dataIndex: "username",
      key: "username",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">{v || "System"}</span>
      ),
    },
    {
      title: "Store Location",
      dataIndex: "store_name",
      key: "store_name",
      render: (v: string) => (
        <span className="text-xs font-semibold text-gray-655">{v}</span>
      ),
    },
    {
      title: "Closed Physical Cash",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-gray-900">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (_: any, record: any) => (
        <Button
          type="link"
          size="small"
          onClick={() =>
            navigate(`/system/reports/sign-off-detail?id=${record.id}`)
          }
          className="text-xs font-bold text-indigo-600 hover:text-indigo-800 animate-none p-0 border-none bg-transparent h-auto"
        >
          View Sign Off Report
        </Button>
      ),
    },
  ];

  return (
    <ReportPage
      title="Register Shifts (Sign Offs)"
      description="Review a ledger of closed register shifts, end of shift physical counts, and cashier drops."
      icon={<FileText className="w-6 h-6 text-purple-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
